import { CloudOff, RefreshCw, ShieldAlert, Wifi, WifiOff, type LucideIcon } from 'lucide-react'
import { useSyncExternalStore } from 'react'

import type { AuthStore } from '../features/auth/auth-store'
import type { SessionDoubt, TerrainSession } from '../features/auth/session'
import { useIsOnline } from './network'

/**
 * **L'état de connexion de la tablette, en une seule valeur.**
 *
 * Il en existait deux, et elles se contredisaient à l'écran : le pied du rail lisait
 * `navigator.onLine` et affichait « En ligne », pendant que la déconnexion juste en dessous se
 * fermait sur l'état de la session et disait « Session non confirmée ». Deux vérités partielles,
 * toutes les deux justes, dont la somme se lit comme un bug — c'est le constat qui a ouvert
 * cette correction (#141).
 *
 * Les deux sources restent nécessaires et aucune ne suffit :
 * - `navigator.onLine` dit qu'une **interface réseau** est active. Il ne dit rien du serveur
 *   GHP — une tablette branchée au Wi-Fi d'un chantier sans route vers Internet est « en ligne » ;
 * - la session dit ce que le **serveur** a répondu au dernier rafraîchissement. Elle ne dit rien
 *   du câble — un jeton frais de la minute d'avant survit à une coupure qui vient d'arriver.
 *
 * D'où une seule fonction qui les compose, et deux écrans qui la lisent.
 */
export type ConnectionStatus =
  'online' | 'checking' | 'unreachable' | 'sign_in_required' | 'offline'

/** Ce qu'un doute sur la session devient à l'écran, une fois le réseau écarté. */
const DOUBT_STATUS: Record<SessionDoubt, ConnectionStatus> = {
  not_attempted: 'checking',
  unreachable: 'unreachable',
  rejected: 'sign_in_required',
}

/**
 * La composition, pure et testable à part.
 *
 * ⚠️ **L'absence de réseau passe devant tout le reste**, y compris devant un jeton que le serveur
 * vient de refuser. Ce n'est pas une hiérarchie de gravité mais d'**utilité** : sans réseau,
 * aucune ressaisie d'identifiants n'aboutira, et proposer la seule action impossible est pire que
 * de ne rien proposer. Le doute sur la session n'est pas perdu pour autant — il est dans la
 * session, et il reparaît au retour du réseau.
 *
 * `signed_out` ne se rend jamais sous la coquille (garde de `shellRoute`) ; la valeur rendue est
 * celle qui décrit le geste à faire, pas une panne.
 */
export function connectionStatusOf(session: TerrainSession, networkUp: boolean): ConnectionStatus {
  if (!networkUp) return 'offline'
  if (session.state === 'online') return 'online'
  if (session.state === 'signed_out') return 'sign_in_required'

  return DOUBT_STATUS[session.reason]
}

/**
 * Le libellé du rail — **court**, la colonne fait 88 px et tout y est tronqué plutôt qu'ajusté.
 * La phrase complète est dans le panneau, qui a la place de l'écrire.
 */
export const CONNECTION_LABEL: Record<ConnectionStatus, string> = {
  online: 'En ligne',
  checking: 'Connexion…',
  unreachable: 'Sans réponse',
  sign_in_required: 'Session expirée',
  offline: 'Hors ligne',
}

/**
 * Ce que l'agent lit dans le panneau : **l'état, puis ce qu'il en coûte**.
 *
 * Aucune de ces phrases n'accuse la tablette ni l'agent, et aucune n'affirme une panne que le
 * code n'a pas constatée (M21) — c'est tout l'objet de `SessionDoubt`. Les trois cas dégradés
 * disent la même chose sur le fond, parce que c'est vrai : **la saisie continue**.
 */
export const CONNECTION_EXPLANATION: Record<ConnectionStatus, string> = {
  online:
    'La tablette parle au serveur GHP. Les contrôles transmis partent, et la déconnexion est ouverte.',
  checking:
    'La tablette a du réseau, et le serveur n’a pas encore confirmé la session. C’est l’état normal des premières secondes après le démarrage : rien n’a échoué.',
  unreachable:
    'La tablette a du réseau, mais le serveur GHP n’a pas répondu au dernier essai. La saisie continue et partira toute seule dès qu’il répondra.',
  sign_in_required:
    'Le serveur a refusé la session de cette tablette : elle a expiré, ou elle a été révoquée depuis le bureau. Attendre ne la rétablira pas — il faut ressaisir les identifiants ci-dessous. Les contrôles en cours restent sur la tablette. Si le bureau a verrouillé ce compte, la ressaisie échouera aussi : la déconnexion, en pied de rail, reste ouverte pour rendre la tablette à une autre équipe.',
  offline:
    'La tablette n’a aucun réseau. La saisie continue et part toute seule au retour du réseau.',
}

/**
 * L'état de connexion vu par un écran.
 *
 * Deux abonnements, un seul rendu : la session (hors de React, comme partout ici) et l'état
 * réseau du navigateur. Un écran qui n'en lirait qu'un afficherait de nouveau deux vérités
 * séparées, ce que cette correction supprime.
 */
export function useConnectionStatus(auth: AuthStore): ConnectionStatus {
  const session = useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot)
  const networkUp = useIsOnline()

  return connectionStatusOf(session, networkUp)
}

/**
 * L'icône du rail et de l'en-tête du panneau — **la même des deux côtés** : c'est le seul
 * indice que l'agent a sous les yeux en permanence, et le retrouver à l'identique dans le
 * panneau lui confirme qu'il a bien ouvert ce sur quoi il a appuyé.
 */
export const CONNECTION_ICON: Record<ConnectionStatus, LucideIcon> = {
  online: Wifi,
  checking: RefreshCw,
  unreachable: CloudOff,
  sign_in_required: ShieldAlert,
  offline: WifiOff,
}
